// CartonGradeImportPage.tsx
import * as React from "react";
import {useCallback, useEffect, useState} from "react";
import {withAuthWrapperComponent} from "@/app/auth/WithAuthWrapperComponent";
import {AppFrame} from "@/app/AppFrame";
import {CircularProgress, Grid2 as Grid} from "@mui/material";
import {CardEx} from "@/lib/components/CardEx";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {getI18n, useTranslation} from "react-i18next";
import {AdminI18N, CommonI18N} from "@/translations";
import {useSnackbarEx} from "@/lib/snackbarex";
import {useDialogController} from "@/lib/dialog-controller";
import ConfirmationDialog from "@/lib/components/ConfirmationDialog";
import {cartonGradeImportApi} from "@/boxhub-api";
import {buildRightAlignedCell, DataTable} from "@/lib/components/DataTable";
import {CartonGradeCatalogImportLogDTO} from "@/api/data-contracts";
import {Refresh as RefreshIcon} from "@mui/icons-material";
import ImportLogDialog from "./ImportLogDialog";
import {formatDateTime, formatDuration, formatInteger} from "@/lib/utils";

const CartonGradeImportPage = () => {
    const {t} = useTranslation();
    const {showMessage} = useSnackbarEx();
    const [DialogNode, showDialog] = useDialogController();
    const [importLogs, setImportLogs] = useState<CartonGradeCatalogImportLogDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [importLog, setImportLog] = useState<CartonGradeCatalogImportLogDTO | null>(null);
    const [showImportLog, setShowImportLog] = useState(false);
    const locale = getI18n().language;

    const loadImportLogs = useCallback(() => {
        setLoading(true);
        cartonGradeImportApi
                .getImportLog()
                .then((response) => {
                    setImportLogs(response.data);
                    setLoading(false);
                })
                .catch((error) => {
                    showMessage({
                        summary: t(AdminI18N.cartonGradesImport.message.importFailed),
                        error,
                        severity: "error"
                    });
                    setLoading(false);
                });
    }, [showMessage, t]);

    useEffect(() => {
        loadImportLogs();
    }, [loadImportLogs]);

    const handleImportCatalog = useCallback(() => {
        showDialog((open, close) => (
                <ConfirmationDialog
                        open={open}
                        onClose={close}
                        title={t(AdminI18N.cartonGradesImport.dialog.confirmTitle)}
                        message={t(AdminI18N.cartonGradesImport.dialog.confirmMessage)}
                        confirmButtonText={t(CommonI18N.message.yes)}
                        cancelButtonText={t(CommonI18N.message.no)}
                        onConfirm={() => {
                            close();
                            setLoading(true);
                            cartonGradeImportApi
                                    .importCatalog()
                                    .then((response) => {
                                        setImportLog(response.data);
                                        setShowImportLog(true);
                                        setLoading(false);
                                        // Aktualisieren der Importprotokolle nach erfolgreichem Import
                                        loadImportLogs();
                                    })
                                    .catch((error) => {
                                        setLoading(false);
                                        showMessage({
                                            summary: t(AdminI18N.cartonGradesImport.message.importFailed),
                                            error,
                                            severity: "error"
                                        });
                                    });
                        }}
                />
        ));
    }, [showDialog, t, showMessage, loadImportLogs]);

    const handleCloseImportLog = useCallback(() => {
        setShowImportLog(false);
    }, []);

    const handleViewImportLog = useCallback((log: CartonGradeCatalogImportLogDTO) => {
        setImportLog(log);
        setShowImportLog(true);
    }, []);

    const columns = [
        {
            header: t(AdminI18N.cartonGradesImport.column.importDate),
            accessorFn: (row: CartonGradeCatalogImportLogDTO) =>
                    row.createdAt ? formatDateTime(new Date(row.createdAt), locale) : "-",
            id: "createdAt"
        },
        {
            header: t(AdminI18N.cartonGradesImport.column.importDuration),
            accessorFn: (row: CartonGradeCatalogImportLogDTO) => {
                const totalDuration = (row.importDuration || 0) / 1000 + (row.downloadDuration || 0) / 1000;
                return formatDuration(totalDuration, t);
            },
            id: "duration"
        },
        {
            header: t(AdminI18N.cartonGradesImport.column.importUser),
            accessorFn: (row: CartonGradeCatalogImportLogDTO) =>
                    row.createdBy ? `${row.createdBy.firstName} ${row.createdBy.lastName}` : "-",
            id: "user"
        },
        {
            header: t(AdminI18N.cartonGradesImport.column.totalGrades),
            accessorFn: (row: CartonGradeCatalogImportLogDTO) => formatInteger(row.cartonGradeCount, locale) || 0,
            cell: buildRightAlignedCell,
            id: "totalGrades"
        },
        {
            header: t(AdminI18N.cartonGradesImport.column.updatedGrades),
            accessorFn: (row: CartonGradeCatalogImportLogDTO) => formatInteger(row.modifiedCartonGrades, locale) || 0,
            cell: buildRightAlignedCell,
            id: "updatedGrades"
        },
        {
            header: t(AdminI18N.cartonGradesImport.column.newGrades),
            accessorFn: (row: CartonGradeCatalogImportLogDTO) => formatInteger(row.createdCartonGrades, locale) || 0,
            cell: buildRightAlignedCell,
            id: "newGrades"
        },
        {
            header: t(AdminI18N.cartonGradesImport.column.removedGrades),
            accessorFn: (row: CartonGradeCatalogImportLogDTO) => formatInteger(row.deletedCartonGrades, locale) || 0,
            cell: buildRightAlignedCell,
            id: "removedGrades"
        },
        {
            header: t(AdminI18N.cartonGradesImport.column.totalPlants),
            accessorFn: (row: CartonGradeCatalogImportLogDTO) => formatInteger(row.plantCount, locale) || 0,
            cell: buildRightAlignedCell,
            id: "totalPlants"
        },
        {
            header: t(AdminI18N.cartonGradesImport.column.knownPlants),
            accessorFn: (row: CartonGradeCatalogImportLogDTO) => formatInteger(row.modifiedPlants, locale) || 0,
            cell: buildRightAlignedCell,
            id: "knownPlants"
        },
        {
            header: t(AdminI18N.cartonGradesImport.column.newPlants),
            accessorFn: (row: CartonGradeCatalogImportLogDTO) => formatInteger(row.createdPlants, locale) || 0,
            cell: buildRightAlignedCell,
            id: "newPlants"
        },
        {
            header: t(AdminI18N.cartonGradesImport.column.removedPlants),
            accessorFn: (row: CartonGradeCatalogImportLogDTO) => formatInteger(row.deletedPlants, locale) || 0,
            cell: buildRightAlignedCell,
            id: "removedPlants"
        },
        {
            header: t(AdminI18N.cartonGradesImport.page.overallStatusTitle),
            accessorFn: (row: CartonGradeCatalogImportLogDTO) =>
                    row.success ? t(AdminI18N.cartonGradesImport.message.statusSuccess) : t(AdminI18N.cartonGradesImport.message.statusFailed),
            id: "status"
        }
    ];

    const importLogsTableButtons = (
            <Grid container direction="row" spacing={2}>
                <Grid>
                    <ButtonEx
                            label={t(AdminI18N.cartonGradesImport.action.import)}
                            startIcon={<RefreshIcon/>}
                            onClick={handleImportCatalog}
                            color="primary"
                            data-cy="import-catalog-button"
                    />
                </Grid>
            </Grid>
    );

    return (
            <AppFrame title={t(AdminI18N.cartonGradesImport.page.title)} buttons={importLogsTableButtons}>
                <Grid container spacing={2} paddingTop={1}>
                    <Grid size={{xs: 12}}>
                        <CardEx title={t(AdminI18N.cartonGradesImport.page.cardTitle)}>
                            <Grid container spacing={2} direction="column" width="100%">
                                <Grid size={{xs: 12}}>
                                    {loading ? (
                                            <CircularProgress/>
                                    ) : (
                                            <DataTable
                                                    columns={columns}
                                                    data={importLogs}
                                                    striped
                                                    onRowClick={handleViewImportLog}
                                                    noResultsValue={t(AdminI18N.cartonGradesImport.message.noGradesFound)}
                                            />
                                    )}
                                </Grid>
                            </Grid>
                        </CardEx>
                    </Grid>
                </Grid>

                {importLog &&
                        <ImportLogDialog open={showImportLog} onClose={handleCloseImportLog} importLog={importLog}/>}

                <DialogNode/>
            </AppFrame>
    );
};

export default withAuthWrapperComponent(CartonGradeImportPage);
